import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  DragOverlay,
  pointerWithin,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { Plus, Trash2, Pencil, Check, X, FlaskConical, GripVertical, ChevronDown, ChevronUp } from "lucide-react";
import apiClient from "@/api/client";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { formatAmount } from "@/utils/formatAmount";
import { formatDate } from "@/utils/formatDate";
import type { Project } from "@/features/enterprises/types";
import {
  useProjectGroups,
  useCreateProjectGroup,
  useUpdateProjectGroup,
  useDeleteProjectGroup,
} from "@/features/enterprises/hooks/useProjectGroups";
import { projectGroupKeys } from "@/features/enterprises/hooks/useProjectGroups";

// Sortowanie projektów w grupie: po skrócie + nazwie, naturalnie (TD2 < TD13, nie alfabetycznie "TD13" < "TD2").
const projectCollator = new Intl.Collator("pl", { numeric: true, sensitivity: "base" });
const projectSortKey = (p: Project) => `${p.short_name ?? ""} ${p.name ?? ""}`;

// Ile projektów pokazać domyślnie; resztę chowamy za przełącznikiem „Pokaż wszystkie".
const VISIBLE_PROJECTS = 5;

/**
 * Zarządzanie grupami przedsięwzięć (CEO) — tablica z przeciąganiem.
 * Grupa to jednostka planistyczna/kosztowa. Projekty przeciągasz między grupami;
 * przełącznik „Rozliczane wspólnie" mówi, czy grupę traktować jako jedno rozliczenie.
 */
export function ProjectGroupsListPage() {
  const { data: groups = [], isLoading } = useProjectGroups();
  const { data: projects = [], isLoading: projectsLoading } = useQuery({
    queryKey: ["projects", "for-grouping"],
    queryFn: () => apiClient.get<{ data: Project[] }>("/projects/").then((r) => r.data.data),
  });
  const createGroup = useCreateProjectGroup();
  const deleteGroup = useDeleteProjectGroup();
  const qc = useQueryClient();

  const reassign = useMutation({
    mutationFn: ({ projectId, group }: { projectId: string; group: string }) =>
      apiClient.patch(`/projects/${projectId}/`, { group }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: projectGroupKeys.all });
    },
  });

  const [newName, setNewName] = useState("");
  const [newShort, setNewShort] = useState("");
  const [err, setErr] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  // Projekty pogrupowane wg grupy.
  const byGroup = useMemo(() => {
    const m: Record<string, Project[]> = {};
    for (const p of projects) {
      const gid = p.group ?? "__none__";
      (m[gid] ??= []).push(p);
    }
    for (const gid in m) {
      m[gid].sort((a, b) => projectCollator.compare(projectSortKey(a), projectSortKey(b)));
    }
    return m;
  }, [projects]);

  const activeProject = projects.find((p) => p.id === activeId) ?? null;

  const handleCreate = () => {
    if (!newName.trim()) { setErr("Nazwa jest wymagana"); return; }
    setErr("");
    createGroup.mutate(
      { name: newName.trim(), short_name: newShort.trim() },
      { onSuccess: () => { setNewName(""); setNewShort(""); }, onError: () => setErr("Nie udało się utworzyć grupy.") },
    );
  };

  const onDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));
  const onDragEnd = (e: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over) return;
    const projectId = String(active.id);
    const targetGroup = String(over.id);
    const current = active.data.current?.groupId as string | undefined;
    if (!targetGroup || targetGroup === "__none__" || targetGroup === current) return;
    reassign.mutate({ projectId, group: targetGroup });
  };

  if (isLoading || projectsLoading) return <PageLoader />;

  return (
    <div className="max-w-[1400px]">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Grupy przedsięwzięć</h1>
        <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-3" />
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-3xl">
          Jednostka planistyczna i kosztowa. Grupę z jednym projektem traktuj jako duże zlecenie;
          drobne zlecenia łącz w jedną grupę. <b>Przeciągnij</b> projekt między grupami, by go przenieść.
          Przełącznik <b>„Rozliczane wspólnie"</b> mówi, czy grupę liczyć jako jedno rozliczenie.
        </p>
      </div>

      {/* Dodawanie */}
      <div className="bg-white dark:bg-gray-800 p-4 mb-6 flex flex-col sm:flex-row gap-3 sm:items-end rounded-lg">
        <div className="flex-1">
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Nazwa grupy</label>
          <input
            type="text"
            value={newName}
            onChange={(e) => { setNewName(e.target.value); setErr(""); }}
            className={`block w-full rounded-md border px-3 py-2 text-sm ${err ? "border-red-400" : "border-gray-300 dark:border-gray-600"} bg-white dark:bg-gray-800 dark:text-gray-100`}
            placeholder="np. Przyłącza ENERGA — drobnica"
          />
          {err && <p className="text-xs text-red-600 mt-0.5">{err}</p>}
        </div>
        <div className="w-full sm:w-40">
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Skrót</label>
          <input
            type="text"
            value={newShort}
            maxLength={30}
            onChange={(e) => setNewShort(e.target.value.toUpperCase())}
            className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-mono bg-white dark:bg-gray-800 dark:text-gray-100"
            placeholder="np. ENERGA"
          />
        </div>
        <Button onClick={handleCreate} loading={createGroup.isPending}>
          <Plus size={15} className="mr-1" /> Dodaj grupę
        </Button>
      </div>

      {groups.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 py-12 text-center text-sm text-gray-400 dark:text-gray-500 rounded-lg">
          Brak grup. Dodaj pierwszą powyżej.
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))] items-start">
            {groups.map((g) => (
              <GroupColumn
                key={g.id}
                group={g}
                projects={byGroup[g.id] ?? []}
                onDelete={() => deleteGroup.mutate(g.id)}
              />
            ))}
          </div>
          <DragOverlay>
            {activeProject ? <ProjectCardBody project={activeProject} dragging /> : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}

function GroupColumn({
  group,
  projects,
  onDelete,
}: {
  group: {
    id: string;
    name: string;
    short_name: string;
    total_value: string;
    planned_end_date: string | null;
    project_count: number;
    billed_together: boolean;
  };
  projects: Project[];
  onDelete: () => void;
}) {
  const update = useUpdateProjectGroup(group.id);
  const { setNodeRef, isOver } = useDroppable({ id: group.id });
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [name, setName] = useState(group.name);
  const [short, setShort] = useState(group.short_name);

  const visibleProjects = expanded ? projects : projects.slice(0, VISIBLE_PROJECTS);
  const hiddenCount = projects.length - visibleProjects.length;

  const save = () => {
    update.mutate(
      { name: name.trim() || group.name, short_name: short.trim() },
      { onSuccess: () => setEditing(false) },
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex flex-col">
      {/* Nagłówek grupy */}
      <div className="p-3 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            {editing ? (
              <div className="space-y-1.5">
                <input
                  value={short}
                  maxLength={30}
                  onChange={(e) => setShort(e.target.value.toUpperCase())}
                  placeholder="Skrót"
                  className="w-full rounded border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-xs font-mono bg-white dark:bg-gray-800 dark:text-gray-100"
                />
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded border border-gray-300 dark:border-gray-600 px-1.5 py-1 text-sm bg-white dark:bg-gray-800 dark:text-gray-100"
                />
              </div>
            ) : (
              <>
                {group.short_name && <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400">{group.short_name}</div>}
                <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate" title={group.name}>{group.name}</div>
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {editing ? (
              <>
                <button onClick={save} className="text-green-600 hover:text-green-700" title="Zapisz"><Check size={16} /></button>
                <button onClick={() => { setEditing(false); setName(group.name); setShort(group.short_name); }} className="text-gray-400 hover:text-gray-600" title="Anuluj"><X size={16} /></button>
              </>
            ) : (
              <>
                <Link
                  to={`/finances/order-simulations?source=group&group=${group.id}&name=${encodeURIComponent("Symulacja: " + group.name)}`}
                  className="text-gray-400 hover:text-[var(--color-primary)]"
                  title="Symuluj zlecenie dla tej grupy"
                ><FlaskConical size={15} /></Link>
                <button onClick={() => setEditing(true)} className="text-gray-400 hover:text-[var(--color-primary)]" title="Edytuj"><Pencil size={15} /></button>
                <button
                  onClick={onDelete}
                  disabled={projects.length > 0}
                  className="text-gray-400 hover:text-red-600 disabled:opacity-25 disabled:cursor-not-allowed"
                  title={projects.length > 0 ? "Najpierw przenieś projekty z grupy" : "Usuń grupę"}
                ><Trash2 size={15} /></button>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mt-2.5">
          <div className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
            {formatAmount(group.total_value)} <span className="opacity-60">PLN</span>
            {group.planned_end_date && <span className="ml-2 opacity-70">do {formatDate(group.planned_end_date)}</span>}
          </div>
          <BilledTogetherToggle
            value={group.billed_together}
            disabled={update.isPending}
            onChange={(v) => update.mutate({ billed_together: v })}
          />
        </div>
      </div>

      {/* Lista projektów (droppable) */}
      <div
        ref={setNodeRef}
        className={`p-2 min-h-[80px] flex flex-col gap-1.5 rounded-b-lg transition-colors ${isOver ? "bg-blue-50 dark:bg-blue-900/20 ring-2 ring-inset ring-blue-400" : ""}`}
      >
        {projects.length === 0 ? (
          <div className="text-[11px] text-gray-400 dark:text-gray-500 text-center py-4 border border-dashed border-gray-200 dark:border-gray-700 rounded">
            Przeciągnij tu projekt
          </div>
        ) : (
          <>
            {visibleProjects.map((p) => (
              <DraggableProject key={p.id} project={p} groupId={group.id} />
            ))}
            {projects.length > VISIBLE_PROJECTS && (
              <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                className="mt-0.5 flex items-center justify-center gap-1 py-1 text-[11px] font-medium text-[var(--color-primary)] hover:underline"
              >
                {expanded ? (
                  <>
                    <ChevronUp size={13} /> Pokaż mniej
                  </>
                ) : (
                  <>
                    <ChevronDown size={13} /> Pokaż wszystkie ({hiddenCount} więcej)
                  </>
                )}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function DraggableProject({ project, groupId }: { project: Project; groupId: string }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: project.id,
    data: { groupId },
  });
  return (
    <div
      ref={setNodeRef}
      className={isDragging ? "opacity-30" : ""}
      {...attributes}
      {...listeners}
    >
      <ProjectCardBody project={project} />
    </div>
  );
}

function ProjectCardBody({ project, dragging }: { project: Project; dragging?: boolean }) {
  return (
    <div className={`flex items-center gap-2 px-2 py-1.5 rounded border bg-white dark:bg-gray-800 ${dragging ? "border-blue-400 shadow-lg" : "border-gray-200 dark:border-gray-700"} cursor-grab active:cursor-grabbing`}>
      <GripVertical size={14} className="text-gray-300 dark:text-gray-600 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate" title={project.name}>
          {project.short_name ? <span className="font-mono text-gray-400 mr-1">{project.short_name}</span> : null}
          {project.name}
        </div>
        {project.client_name && <div className="text-[10px] text-gray-400 truncate">{project.client_name}</div>}
      </div>
      {project.total_value && (
        <div className="text-[10px] tabular-nums text-gray-400 shrink-0">{formatAmount(project.total_value)}</div>
      )}
    </div>
  );
}

function BilledTogetherToggle({ value, onChange, disabled }: { value: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(!value)}
      title={value ? "Grupa rozliczana wspólnie (jedno rozliczenie). Kliknij, by wyłączyć." : "Grupa rozliczana osobno per projekt. Kliknij, by włączyć wspólne."}
      className="flex items-center gap-1.5 text-[10px] font-medium text-gray-500 dark:text-gray-400"
    >
      <span className={`relative inline-flex h-4 w-7 items-center rounded-full transition ${value ? "bg-[var(--color-primary)]" : "bg-gray-300 dark:bg-gray-600"}`}>
        <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition ${value ? "translate-x-3.5" : "translate-x-0.5"}`} />
      </span>
      Wspólnie
    </button>
  );
}
